import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Paper,
  CircularProgress,
  LinearProgress,
  Divider,
} from '@mui/material';
import {
  TrendingUp,
  Assignment,
  CheckCircle,
  HourglassEmpty,
  PlayArrow,
  Speed as OEEIcon,
} from '@mui/icons-material';
import { LineChart } from '@mui/x-charts/LineChart';
import { useNavigate } from 'react-router-dom';
import { statusColors } from '../App';
import { apiService } from '../services/apiService';
import OEEGauge from '../components/OEEGauge';

interface DashboardStats {
  in_progress: number;
  pending: number;
  completed: number;
  approved: number;
  total_batches: number;
}

interface Batch {
  id: number;
  batch_number: string;
  status: string;
  product_name: string;
  line_name: string;
  created_at: string;
}

interface WeeklyTrend {
  date: string;
  completed_count: number;
  approved_count: number;
}

// Demo OEE data — will be replaced with live data from OEE module
interface AssetOEE {
  asset: string;
  line: string;
  availability: number;
  performance: number;
  quality: number;
  oee: number;
  status: 'running' | 'idle' | 'down' | 'changeover';
}

const demoAssets: AssetOEE[] = [
  { asset: 'TAB-PRESS-01', line: 'Tablet Line 1', availability: 92.4, performance: 88.1, quality: 99.2, oee: 80.8, status: 'running' },
  { asset: 'TAB-PRESS-02', line: 'Tablet Line 2', availability: 87.6, performance: 91.3, quality: 98.7, oee: 78.9, status: 'running' },
  { asset: 'BLENDER-01', line: 'Blending Suite', availability: 95.1, performance: 84.2, quality: 99.8, oee: 79.9, status: 'running' },
  { asset: 'COATER-01', line: 'Tablet Line 1', availability: 78.3, performance: 82.5, quality: 97.4, oee: 62.9, status: 'changeover' },
  { asset: 'GRANULATOR-01', line: 'Blending Suite', availability: 91.7, performance: 86.9, quality: 99.5, oee: 79.3, status: 'idle' },
  { asset: 'ENCAPS-01', line: 'Capsule Line 1', availability: 45.2, performance: 72.1, quality: 94.8, oee: 30.9, status: 'down' },
];

// Calculate site aggregate
const calcSiteOEE = (assets: AssetOEE[]) => {
  const running = assets.filter(a => a.status === 'running' || a.status === 'idle');
  if (running.length === 0) return { availability: 0, performance: 0, quality: 0, oee: 0 };
  const avg = (key: keyof AssetOEE) =>
    running.reduce((sum, a) => sum + (a[key] as number), 0) / running.length;
  const a = avg('availability');
  const p = avg('performance');
  const q = avg('quality');
  return { availability: a, performance: p, quality: q, oee: (a * p * q) / 10000 };
};

const statusIcon = (status: string) => {
  switch (status) {
    case 'running': return { color: '#10B981', label: 'Running' };
    case 'idle': return { color: '#F59E0B', label: 'Idle' };
    case 'down': return { color: '#EF4444', label: 'Down' };
    case 'changeover': return { color: '#8B5CF6', label: 'Changeover' };
    default: return { color: '#6B7280', label: status };
  }
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentBatches, setRecentBatches] = useState<Batch[]>([]);
  const [weeklyTrend, setWeeklyTrend] = useState<WeeklyTrend[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const data = await apiService.getDashboardData();
        setStats(data.stats);
        setRecentBatches(data.recent_batches);
        setWeeklyTrend(data.weekly_trend);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const handleBatchClick = (batchId: number) => {
    navigate(`/batch-execution/${batchId}`);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status: string) => {
    return statusColors[status as keyof typeof statusColors] || '#6B7280';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const chartData = weeklyTrend.map(item => ({
    date: new Date(item.date).toLocaleDateString(),
    completed: item.completed_count,
    approved: item.approved_count,
  }));

  const siteOEE = calcSiteOEE(demoAssets);

  return (
    <Box>
      <Typography variant="h3" component="h1" gutterBottom>
        Overview
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="body2">In Progress</Typography>
                  <Typography variant="h4">{stats?.in_progress || 0}</Typography>
                </Box>
                <PlayArrow sx={{ fontSize: 36, color: statusColors.in_progress }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="body2">Pending</Typography>
                  <Typography variant="h4">{stats?.pending || 0}</Typography>
                </Box>
                <HourglassEmpty sx={{ fontSize: 36, color: statusColors.pending }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="body2">Completed</Typography>
                  <Typography variant="h4">{stats?.completed || 0}</Typography>
                </Box>
                <Assignment sx={{ fontSize: 36, color: statusColors.completed }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="body2">Approved</Typography>
                  <Typography variant="h4">{stats?.approved || 0}</Typography>
                </Box>
                <CheckCircle sx={{ fontSize: 36, color: statusColors.approved }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Batches + Weekly Trend */}
      <Grid container spacing={2}>
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Typography variant="h5" component="h2" gutterBottom>
                Recent Batches
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Batch Number</TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell>Line</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentBatches.map((batch) => (
                      <TableRow key={batch.id} hover>
                        <TableCell>{batch.batch_number}</TableCell>
                        <TableCell>{batch.product_name}</TableCell>
                        <TableCell>{batch.line_name}</TableCell>
                        <TableCell>
                          <Chip
                            label={batch.status.replace('_', ' ')}
                            size="small"
                            sx={{
                              backgroundColor: getStatusColor(batch.status),
                              color: 'white',
                              textTransform: 'capitalize',
                            }}
                          />
                        </TableCell>
                        <TableCell>{formatDate(batch.created_at)}</TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleBatchClick(batch.id)}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Card>
            <CardContent>
              <Typography variant="h5" component="h2" gutterBottom>
                Weekly Trend
              </Typography>
              {chartData.length > 0 ? (
                <LineChart
                  width={350}
                  height={300}
                  series={[
                    {
                      data: chartData.map(item => item.completed),
                      label: 'Completed',
                      color: statusColors.completed,
                    },
                    {
                      data: chartData.map(item => item.approved),
                      label: 'Approved',
                      color: statusColors.approved,
                    },
                  ]}
                  xAxis={[{
                    scaleType: 'point',
                    data: chartData.map(item => item.date),
                  }]}
                />
              ) : (
                <Typography color="textSecondary">
                  No trend data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* OEE Section */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <OEEIcon sx={{ color: '#3B82F6' }} />
            <Typography variant="h5" component="h2">
              Site OEE — Real-Time
            </Typography>
            <Chip label="DEMO" size="small" sx={{ ml: 1, backgroundColor: '#8B5CF620', color: '#8B5CF6', fontSize: '0.65rem', height: 20 }} />
          </Box>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
              <OEEGauge value={siteOEE.oee} label="SITE OEE" size={220} thickness={18} subtitle="All Assets Aggregate" />
            </Grid>
            <Grid item xs={12} md={5}>
              <Box display="flex" justifyContent="center" gap={1} flexWrap="wrap">
                <OEEGauge value={siteOEE.availability} label="Availability" size={130} thickness={10} color="#3B82F6" />
                <OEEGauge value={siteOEE.performance} label="Performance" size={130} thickness={10} color="#3B82F6" />
                <OEEGauge value={siteOEE.quality} label="Quality" size={130} thickness={10} color="#3B82F6" />
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ p: 1 }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>Site Summary</Typography>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="body2">Assets Online</Typography>
                  <Typography variant="body2" fontWeight="bold" color="#10B981">{demoAssets.filter(a => a.status === 'running').length}/{demoAssets.length}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="body2">Idle</Typography>
                  <Typography variant="body2" fontWeight="bold" color="#F59E0B">{demoAssets.filter(a => a.status === 'idle').length}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="body2">Down</Typography>
                  <Typography variant="body2" fontWeight="bold" color="#EF4444">{demoAssets.filter(a => a.status === 'down').length}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="body2">Changeover</Typography>
                  <Typography variant="body2" fontWeight="bold" color="#8B5CF6">{demoAssets.filter(a => a.status === 'changeover').length}</Typography>
                </Box>
                <Divider sx={{ my: 1, borderColor: '#374151' }} />
                <Typography variant="body2" color="textSecondary" gutterBottom>World Class Target</Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <LinearProgress variant="determinate" value={Math.min((siteOEE.oee / 85) * 100, 100)}
                    sx={{ flexGrow: 1, height: 8, borderRadius: 4, backgroundColor: '#374151',
                      '& .MuiLinearProgress-bar': { backgroundColor: siteOEE.oee >= 85 ? '#10B981' : siteOEE.oee >= 60 ? '#F59E0B' : '#EF4444', borderRadius: 4 } }} />
                  <Typography variant="caption" color="textSecondary">85%</Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: '0.95rem' }}>Asset Breakdown</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 'bold', color: '#9CA3AF', borderBottom: '2px solid #374151', py: 0.75, fontSize: '0.8rem' } }}>
                    <TableCell>Asset</TableCell>
                    <TableCell>Line</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">OEE</TableCell>
                    <TableCell align="center">A</TableCell>
                    <TableCell align="center">P</TableCell>
                    <TableCell align="center">Q</TableCell>
                    <TableCell>OEE Bar</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {demoAssets.sort((a, b) => b.oee - a.oee).map((asset) => {
                    const st = statusIcon(asset.status);
                    return (
                      <TableRow key={asset.asset} hover sx={{ '& td': { py: 0.75, fontSize: '0.8rem' } }}>
                        <TableCell><Typography variant="body2" fontWeight="bold" fontSize="0.8rem">{asset.asset}</Typography></TableCell>
                        <TableCell>{asset.line}</TableCell>
                        <TableCell align="center">
                          <Chip label={st.label} size="small" sx={{ backgroundColor: `${st.color}20`, color: st.color, fontWeight: 'bold', fontSize: '0.7rem', height: 22 }} />
                        </TableCell>
                        <TableCell align="center">
                          <Typography fontWeight="bold" fontSize="0.85rem" color={asset.oee >= 85 ? '#10B981' : asset.oee >= 60 ? '#F59E0B' : '#EF4444'}>{asset.oee.toFixed(1)}%</Typography>
                        </TableCell>
                        <TableCell align="center">{asset.availability.toFixed(1)}%</TableCell>
                        <TableCell align="center">{asset.performance.toFixed(1)}%</TableCell>
                        <TableCell align="center">{asset.quality.toFixed(1)}%</TableCell>
                        <TableCell sx={{ minWidth: 120 }}>
                          <LinearProgress variant="determinate" value={asset.oee}
                            sx={{ height: 8, borderRadius: 4, backgroundColor: '#374151',
                              '& .MuiLinearProgress-bar': { backgroundColor: asset.oee >= 85 ? '#10B981' : asset.oee >= 60 ? '#F59E0B' : '#EF4444', borderRadius: 4 } }} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Dashboard;
